import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { LeadCTAs } from "@/components/LeadCTAs";
import { submitWebsiteLead } from "@/api/submitWebsiteLead";
import { consumeLeadPrefill } from "@/utils/openLeadForm";
import { reportGoogleAdsLeadConversion } from "@/utils/googleAdsConversion";

const BUDGETS = ["₹1.5 Cr – ₹2.5 Cr", "₹2.5 Cr – ₹4 Cr", "₹4 Cr – ₹6 Cr", "Above ₹6 Cr"];

const initial = { name: "", phone: "", email: "", budget: "", message: "" };

export const LeadForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const prefill = consumeLeadPrefill();
    if (prefill?.message) {
      setForm((f) => ({ ...f, message: prefill.message }));
    }
  }, []);

  const postLead = async (action) => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    const actionNote =
      action === "visit"
        ? "Request type: Book site visit"
        : action === "callback"
          ? "Request type: Request callback"
          : action === "prices"
            ? "Request type: Check prices"
            : "Request type: Enquire";

    const message = [actionNote, form.message?.trim()].filter(Boolean).join("\n\n");

    setSubmitting(true);
    try {
      await submitWebsiteLead({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email?.trim() || undefined,
        budget: form.budget?.trim() || undefined,
        message,
      });
      reportGoogleAdsLeadConversion();
      toast.success(
        action === "visit"
          ? "Site visit request received! Our team will confirm shortly."
          : action === "callback"
            ? "Callback requested! We'll call you soon."
            : "Thank you! Our team will reach out shortly with pricing details."
      );
      setForm(initial);
    } catch (err) {
      toast.error(err.message || "Could not submit your request. Please try WhatsApp or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (action) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await postLead(action);
  };

  return (
    <section id="enquire" className="cp-section cp-section--cream" data-testid="lead-form-section">
      <div className="cp-container cp-lead-grid">
        <Reveal>
          <span className="cp-eyebrow">Get In Touch</span>
          <h2 className="cp-h2">Reserve Your Central Plaza SCO</h2>
          <p className="cp-section-desc">
            Share your details and our sales team will connect with tailored unit and pricing
            options for Central Plaza, Sector 10A, Kharkhoda.
          </p>
          <LeadCTAs
            theme="stack"
            enquireTestId="lead-sidebar-enquire-btn"
            waTestId="lead-sidebar-whatsapp-btn"
          />
        </Reveal>

        <Reveal delay={0.12} className="cp-lead-form-card">
          <form onSubmit={submit("enquire")} data-testid="lead-form">
            <div className="cp-form-row">
              <label htmlFor="lf-name">Name</label>
              <input
                id="lf-name"
                data-testid="lead-form-name-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Full name"
                required
                disabled={submitting}
              />
            </div>
            <div className="cp-form-row cp-form-row--split">
              <div>
                <label htmlFor="lf-phone">Phone</label>
                <input
                  id="lf-phone"
                  data-testid="lead-form-phone-input"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 00000 00000"
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label htmlFor="lf-email">Email</label>
                <input
                  id="lf-email"
                  type="email"
                  data-testid="lead-form-email-input"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@email.com"
                  disabled={submitting}
                />
              </div>
            </div>
            <div className="cp-form-row">
              <label htmlFor="lf-budget">Investment Budget</label>
              <select
                id="lf-budget"
                data-testid="lead-form-budget-select"
                value={form.budget}
                onChange={(e) => set("budget", e.target.value)}
                disabled={submitting}
              >
                <option value="">Select a range</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div className="cp-form-row">
              <label htmlFor="lf-message">Message</label>
              <textarea
                id="lf-message"
                data-testid="lead-form-message-input"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us what you're looking for..."
                rows={3}
                disabled={submitting}
              />
            </div>
            <div className="cp-form-actions">
              <button
                type="submit"
                className="btn btn-gold"
                disabled={submitting}
                data-testid="lead-form-enquire-submit-btn"
              >
                {submitting ? "Sending…" : "Enquire Now"}
              </button>
              <button
                type="button"
                onClick={submit("visit")}
                className="btn btn-outline-dark"
                disabled={submitting}
                data-testid="lead-form-book-visit-btn"
              >
                Book Site Visit
              </button>
              <button
                type="button"
                onClick={submit("callback")}
                className="btn btn-ghost-dark"
                disabled={submitting}
                data-testid="lead-form-callback-btn"
              >
                Request Callback
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
